import type { CommandInputProps } from '../SearchSuggestRadixCommand';
import type { Suggestion as LocationSuggestion } from '~/composables/useLocationAutocomplete';
import type { PropertyFilters } from '~/core/entities/property/filters';
import type { Address, AddressData } from '~~/core/entities/Addresses';
import type { LocationProviders } from '~~/core/ports/repositories/LocationAutocomplete';
import type { FunctionalComponent, HTMLAttributes, SVGAttributes } from 'vue';

export type Item = Address | AddressData;

interface SuggestionResult {
	title: string;
	subtitle: string;
}

type SearchType =
	| 'region'
	| 'city'
	| 'street'
	| 'point_of_interest'
	| 'condo'
	| 'code'
	| 'macroregion';

export interface SearchParams {
	regions: PropertyFilters['regions'];
	cities: PropertyFilters['cities'];
	macroregions: PropertyFilters['macroregions'];
	states?: PropertyFilters['states'];
	placeId?: string | null;
	condoSlug: string | null;
	code?: string | null;
	lat?: number;
	lng?: number;
	text?: string;
}

export interface SuggestionCondoResult extends SearchParams {
	textSearched: string;
	condoName: string;
}

export enum MacroregionIdentifier {
	SAO_PAULO = 'sao_paulo',
	INTERIOR = 'interior',
	LITORAL_NORTE = 'litoral_norte',
	LITORAL_SUL = 'litoral_sul',
	CURITIBA = 'curitiba',
	ALPHAVILLE = 'alphaville'
}

export interface Suggestion {
	result: SuggestionResult;
	id: string | MacroregionIdentifier;
	searchParams: SearchParams;
	searchType: SearchType;
	type?: 'research' | 'suggestion';
	available?: boolean;
	macroregionUsed?: string;
}

export type SuggestionGroupKey =
	| 'regions'
	| 'cities'
	| 'macroregions'
	| 'streets'
	| 'pointsOfInterest'
	| 'condos'
	| 'lastResearchs';

type Suggestions = {
	regions: Suggestion[];
	cities: Suggestion[];
	macroregions: Suggestion[];
	streets: Suggestion[];
	pointsOfInterest: Suggestion[];
	condos: Suggestion[];
	lastResearchs: Suggestion[];
};

interface GroupClassesProps {
	class?: HTMLAttributes['class'];
	groupHeadingClasses?: HTMLAttributes['class'];
	groupIconClasses?: HTMLAttributes['class'];
	groupItemWrapperClasses?: HTMLAttributes['class'];
	groupItemTextClasses?: HTMLAttributes['class'];
	groupItemSubTextClasses?: HTMLAttributes['class'];
}

export interface CodeGroupProps extends GroupClassesProps {
	searchTerm: string;
	suggestionKey: 'code';
	suggestions: Suggestion[];
	isFetchingPropertyByCodeSearch?: boolean;
}

export interface NewGroupProps extends GroupClassesProps {
	searchTerm: string;
	heading: {
		text: string;
		icon: FunctionalComponent<SVGAttributes, {}, any, {}>;
	};
	suggestionKey: SuggestionGroupKey;
	suggestions: LocationSuggestion[];
	isSelection?: boolean;
	isRemovable?: boolean;
}

export interface GroupProps extends GroupClassesProps {
	state: string;
	searchTerm: string;
	heading: {
		text: string;
		icon: FunctionalComponent<SVGAttributes, {}, any, {}>;
	};
	groupKey: 'city' | 'region' | 'macroregion';
	groupItems: Item[];
	isSelection?: boolean;
}

// ====================
// Style Configuration Interfaces
// ====================

/**
 * Configuração de estilos para os grupos de sugestões
 */
export interface GroupStyleConfig {
	groupWrapperClasses?: HTMLAttributes['class'];
	groupHeadingClasses?: HTMLAttributes['class'];
	groupIconClasses?: HTMLAttributes['class'];
	groupItemWrapperClasses?: HTMLAttributes['class'];
	groupItemTextClasses?: HTMLAttributes['class'];
	groupItemSubTextClasses?: HTMLAttributes['class'];
}

/**
 * Configuração de estilos para o input
 */
export interface InputStyleConfig {
	inputWrapperClasses?: HTMLAttributes['class'];
	inputWrapperFilledClasses?: HTMLAttributes['class'];
	inputClasses?: HTMLAttributes['class'];
	inputClearIconWrapperClasses?: HTMLAttributes['class'];
	inputClearIconClasses?: HTMLAttributes['class'];
	inputSearchIconWrapperClasses?: HTMLAttributes['class'];
	inputSearchIconClasses?: HTMLAttributes['class'];
}

/**
 * Configuração de estilos para o estado vazio
 */
export interface EmptyStateStyleConfig {
	emptyStateWrapperClasses?: HTMLAttributes['class'];
	emptyStateIconWrapperClasses?: HTMLAttributes['class'];
	emptyStateIconSubWrapperClasses?: HTMLAttributes['class'];
	emptyStateIconClasses?: HTMLAttributes['class'];
	emptyStateTitleClasses?: HTMLAttributes['class'];
	emptyStateDescriptionClasses?: HTMLAttributes['class'];
}

/**
 * Configuração completa de estilos
 */
export interface StyleConfig
	extends GroupStyleConfig, InputStyleConfig, EmptyStateStyleConfig {
	class?: HTMLAttributes['class'];
	buttonInputClasses?: HTMLAttributes['class'];
	listWrapperClasses?: HTMLAttributes['class'];
	lastResearchsIconClasses?: HTMLAttributes['class'];
}

/**
 * Configuração de comportamento
 */
export interface BehaviorConfig {
	enableAnimation?: boolean;
	closeOnClickOutside?: boolean;
	closeOnSelect?: boolean;
	showCloseIcon?: boolean;
}

/**
 * Configuração de dados de localização
 */
export interface LocationDataConfig {
	regions?: PropertyFilters['regions'];
	cities?: PropertyFilters['cities'];
	macroregions?: PropertyFilters['macroregions'];
	locationSlug?: PropertyFilters['locationSlug'];
}

interface SearchSuggestClassesProps {
	class?: HTMLAttributes['class'];
	buttonInputClasses?: HTMLAttributes['class'];
	listWrapperClasses?: HTMLAttributes['class'];
	groupWrapperClasses?: HTMLAttributes['class'];
	groupHeadingClasses?: HTMLAttributes['class'];
	groupIconClasses?: HTMLAttributes['class'];
	groupItemWrapperClasses?: HTMLAttributes['class'];
	groupItemTextClasses?: HTMLAttributes['class'];
	groupItemSubTextClasses?: HTMLAttributes['class'];
	emptyStateWrapperClasses?: HTMLAttributes['class'];
	emptyStateIconWrapperClasses?: HTMLAttributes['class'];
	emptyStateIconSubWrapperClasses?: HTMLAttributes['class'];
	emptyStateIconClasses?: HTMLAttributes['class'];
	emptyStateTitleClasses?: HTMLAttributes['class'];
	emptyStateDescriptionClasses?: HTMLAttributes['class'];
	inputWrapperClasses?: HTMLAttributes['class'];
	inputWrapperFilledClasses?: HTMLAttributes['class'];
	inputClasses?: HTMLAttributes['class'];
	inputClearIconWrapperClasses?: HTMLAttributes['class'];
	inputClearIconClasses?: HTMLAttributes['class'];
	inputSearchIconWrapperClasses?: HTMLAttributes['class'];
	inputSearchIconClasses?: HTMLAttributes['class'];
	lastResearchsIconClasses?: HTMLAttributes['class'];
}

export interface SearchSuggestProps extends SearchSuggestClassesProps {
	regions: PropertyFilters['regions'];
	cities: PropertyFilters['cities'];
	macroregions: PropertyFilters['macroregions'];
	locationSlug?: PropertyFilters['locationSlug'];
	poiLocationLat?: PropertyFilters['poiLocationLat'];
	poiLocationLng?: PropertyFilters['poiLocationLng'];
	inputPlaceholder?: string;
	isFetchingPropertyByCodeSearch?: boolean;
}

export interface NewSearchSuggestProps extends SearchSuggestClassesProps {
	regions?: PropertyFilters['regions'];
	cities?: PropertyFilters['cities'];
	macroregions?: PropertyFilters['macroregions'];
	locationSlug?: PropertyFilters['locationSlug'];
	placeholders?: string[] | string;
	typingPeriod?: number;
	typingSpeed?: number;
	isFetchingPropertyByCodeSearch?: boolean;
	closeOnClickOutside?: boolean;
	showInputButton?: boolean;
	closeOnSelect?: boolean;
	showCloseIcon?: boolean;
	searchTerm?: string;
	enableAnimation?: boolean;
	currentFilters?: PropertyFilters;
	selectOnly?: boolean;
	hideCodeSearch?: boolean;
	providers?: LocationProviders[];
	placeholderClasses?: HTMLAttributes['class'];
	variant?: 'primary' | 'dark';
	/** Bus para emitir seleções quando selectOnly=false. Default: searchSuggestBus. Tipo: SearchSuggestSelectionBus de useNewSearchSuggest */
	bus?: { emit(event: unknown): void };
	/** Quando true, usa o placeholder dinâmico derivado do composable em vez do animado. */
	useDynamicPlaceholder?: boolean;
	/**
	 * Quando true, o evento `emptyResultOnFilter` só é emitido após o autocomplete
	 * executar para o termo atual (evita falso positivo durante a digitação).
	 */
	emitEmptyStateOnlyAfterAutocomplete?: boolean;
	/**
	 * Quando true, inclui `textSearched` no payload de seleção emitido pelo suggest.
	 * Usado para tracking de Shorts sem alterar outros fluxos.
	 */
	includeTextSearchedInSelectionPayload?: boolean;
}

/**
 * Props do campo interno (dropdown). `Suggest.vue` expõe só {@link NewSearchSuggestProps};
 * Shorts desktop usa o wrapper da barra Shorts com estes flags.
 */
export interface SearchSuggestFieldProps extends NewSearchSuggestProps {
	/** Oculta ruas e POIs do grupo “Últimas buscas”. */
	excludeStreetAndPoiFromLastResearchs?: boolean;
	/** Quando false, mantém o termo após escolher sugestão (ex.: label controlado pelo pai). */
	resetSearchTermOnSelect?: boolean;
	/** Força o placeholder do input quando `inputFocusedPlaceholder` tem prioridade na árvore de placeholders. */
	inputFocusedPlaceholder?: string;
	/** Formato do rótulo para sugestão tipo região (vírgula vs traço). */
	regionSelectTextFormat?: 'default' | 'region-comma';
}

export interface GroupItemClickEvent {
	item: Item;
	groupKey: 'city' | 'region' | 'macroregion';
}

export interface NewSuggestInputProps
	extends
		Pick<
			SearchSuggestClassesProps,
			| 'inputWrapperClasses'
			| 'inputClasses'
			| 'inputClearIconWrapperClasses'
			| 'inputClearIconClasses'
			| 'inputSearchIconWrapperClasses'
			| 'inputSearchIconClasses'
		>,
		Pick<
			NewSearchSuggestProps,
			'placeholders' | 'typingPeriod' | 'typingSpeed'
		>,
		CommandInputProps {
	enableAnimation?: boolean;
	placeholder?: string;
	showCloseIcon?: boolean;
	focusedPlaceholder?: string;
}
